import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  setCommitments,
  type CommitmentChange,
} from 'common/src/api/commercialProgram';
import {
  PROGRAM_UNIT_LABELS,
  formatProgramQuantity,
  type CommittedMatrix,
} from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  ExporterReceiver,
  ReceiverMapping,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, StatusChip } from '../../components/ui';
import { exportProgramToExcel } from './exportProgram';
import type { CommercialProgramState } from './useCommercialProgram';

interface ImportExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: CommercialProgramState;
  program: CommercialProgram;
  currentMatrix: CommittedMatrix;
}

interface ParsedRow {
  clientName: string;
  values: Map<number, number>;
}

const IGNORE = 'ignore';

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9\u4e00-\u9fff]+/g, ' ')
    .trim();

// Razón social: no ayuda a distinguir clientes ("Pacific Fruit Trading LLC"
// y "Pacific Fruit Trading" son el mismo).
const COMPANY_WORDS = new Set([
  'LLC',
  'LTD',
  'LTDA',
  'LIMITED',
  'INC',
  'CO',
  'CORP',
  'CORPORATION',
  'COMPANY',
  'SA',
  'SPA',
  'SRL',
  'GMBH',
  'BV',
  'PTE',
  'PLC',
  'AND',
  'THE',
]);
const CHINESE_COMPANY_WORDS = /有限责任公司|股份有限公司|有限公司|公司/g;

const coreName = (name: string) =>
  normalize(name.replace(CHINESE_COMPANY_WORDS, ' '))
    .split(' ')
    .filter((word) => word && !COMPANY_WORDS.has(word))
    .join(' ');

/** Parecido entre dos nombres (0 a 1), por pares de letras. */
const similarity = (a: string, b: string) => {
  const pairs = (text: string) => {
    const compact = text.replace(/ /g, '');
    const list: string[] = [];
    for (let i = 0; i < compact.length - 1; i += 1)
      list.push(compact.slice(i, i + 2));
    return list;
  };
  const left = pairs(a);
  const right = pairs(b);
  if (!left.length || !right.length) return a === b ? 1 : 0;
  const pool = [...right];
  let common = 0;
  for (const pair of left) {
    const index = pool.indexOf(pair);
    if (index >= 0) {
      common += 1;
      pool.splice(index, 1);
    }
  }
  return (2 * common) / (left.length + right.length);
};

type MatchKind = 'exact' | 'similar' | 'none';

/**
 * Recibidor que corresponde a un nombre del Excel. Primero el nombre igual
 * (o el alias de Frutic); si no, uno parecido, solo si hay uno claro.
 */
const matchReceiver = (
  clientName: string,
  receivers: ExporterReceiver[],
  aliases: ReceiverMapping[]
): { id: number | null; kind: MatchKind } => {
  const name = normalize(clientName);
  const exact =
    receivers.find((r) => normalize(r.name) === name) ??
    receivers.find(
      (r) =>
        r.id ===
        aliases.find((a) => normalize(a.frutic_receiver_name) === name)
          ?.exporter_receiver_id
    );
  if (exact) return { id: exact.id, kind: 'exact' };

  const core = coreName(clientName);
  if (!core) return { id: null, kind: 'none' };
  const candidates = [
    ...receivers.map((r) => ({ id: r.id, core: coreName(r.name) })),
    ...aliases.map((a) => ({
      id: a.exporter_receiver_id,
      core: coreName(a.frutic_receiver_name),
    })),
  ].filter((c) => c.core && receivers.some((r) => r.id === c.id));
  const unique = (ids: number[]) => {
    const distinct = Array.from(new Set(ids));
    return distinct.length === 1 ? distinct[0] : null;
  };

  // Mismo nombre sin la razón social.
  const sameCore = unique(
    candidates.filter((c) => c.core === core).map((c) => c.id)
  );
  if (sameCore) return { id: sameCore, kind: 'similar' };

  // Todas las palabras de uno están en el otro ("Pacific" ↔ "Pacific Fruit").
  const words = (text: string) => text.split(' ').filter((w) => w.length >= 3);
  const contains = (long: string, short: string) =>
    words(short).length > 0 &&
    words(short).every((w) => long.split(' ').includes(w));
  const contained = unique(
    candidates
      .filter((c) => contains(c.core, core) || contains(core, c.core))
      .map((c) => c.id)
  );
  if (contained) return { id: contained, kind: 'similar' };

  // Escrito parecido ("Pacfic Fruit" ↔ "Pacific Fruit"): el mejor, si se
  // distingue claro del segundo.
  const scored = candidates
    .map((c) => ({ id: c.id, score: similarity(core, c.core) }))
    .sort((a, b) => b.score - a.score);
  const best = scored[0];
  const runnerUp = scored.find((c) => c.id !== best?.id);
  if (
    best &&
    best.score >= 0.75 &&
    (!runnerUp || best.score - runnerUp.score >= 0.1)
  )
    return { id: best.id, kind: 'similar' };

  return { id: null, kind: 'none' };
};

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Texto con coma decimal ("1.234,5") o con punto ("0.5").
  const text = String(value).trim();
  const n = Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
  );
  return Number.isFinite(n) ? n : null;
};

/**
 * Lee el Excel del programa: busca la fila con "Cliente" y columnas de semana
 * ("S42" o "42"). Celdas vacías = sin cambio; 0 = sin compromiso.
 */
const parseWorkbook = (data: ArrayBuffer, weeks: number[]) => {
  const workbook = XLSX.read(data, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: null,
  });

  const headerIndex = grid.findIndex((row) =>
    row.some((cell) => normalize(String(cell ?? '')) === 'CLIENTE')
  );
  if (headerIndex < 0) throw new Error('No encontramos la columna "Cliente".');
  const header = grid[headerIndex].map((cell) => String(cell ?? '').trim());
  const clientCol = header.findIndex((h) => normalize(h) === 'CLIENTE');
  const weekCols = header
    .map((h, col) => {
      const match = h.match(/^S?\s*(\d{1,2})$/i);
      const week = match ? Number(match[1]) : null;
      return week != null && weeks.includes(week) ? { col, week } : null;
    })
    .filter((x): x is { col: number; week: number } => x !== null);
  if (weekCols.length === 0)
    throw new Error('No encontramos columnas de semana (ej. S42).');

  const rows: ParsedRow[] = [];
  for (const row of grid.slice(headerIndex + 1)) {
    const clientName = String(row[clientCol] ?? '').trim();
    if (!clientName || normalize(clientName) === 'TOTAL') continue;
    const values = new Map<number, number>();
    for (const { col, week } of weekCols) {
      const value = parseNumber(row[col]);
      if (value != null && value >= 0) values.set(week, value);
    }
    rows.push({ clientName, values });
  }
  return rows;
};

/** Carga masiva: plantilla, match de clientes y vista previa de cambios. */
export default function ImportExcelModal({
  isOpen,
  onClose,
  state,
  program,
  currentMatrix,
}: ImportExcelModalProps) {
  const { t } = useTranslation('commitments');
  const {
    receivers,
    receiverMappings,
    weeks,
    reloadProgramData,
    season,
    species,
    notes,
  } = state;
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [matches, setMatches] = useState<Record<string, string>>({});
  // Cómo calzó cada nombre al leer el Excel: los que no son iguales se revisan.
  const [matchKinds, setMatchKinds] = useState<Record<string, MatchKind>>({});
  const [saving, setSaving] = useState(false);

  const handleFile = async (file: File) => {
    try {
      const parsed = parseWorkbook(await file.arrayBuffer(), weeks);
      setFileName(file.name);
      setRows(parsed);
      // Match automático por nombre; lo que no calce se elige a mano.
      const initial: Record<string, string> = {};
      const kinds: Record<string, MatchKind> = {};
      parsed.forEach((row) => {
        const match = matchReceiver(
          row.clientName,
          receivers,
          receiverMappings
        );
        initial[row.clientName] = match.id ? String(match.id) : '';
        kinds[row.clientName] = match.kind;
      });
      setMatches(initial);
      setMatchKinds(kinds);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(
        t('No se pudo leer el Excel: {{error}}', { error: t(message) })
      );
    }
  };

  const unmatched = (rows || []).filter((row) => !matches[row.clientName]);
  // Nombres que no calzaron exacto: se muestran para revisarlos.
  const toReview = (rows || []).filter(
    (row) => matchKinds[row.clientName] !== 'exact'
  );
  const similarCount = toReview.filter(
    (row) => matchKinds[row.clientName] === 'similar'
  ).length;
  // Dos filas del Excel al mismo recibidor: una pisaría a la otra.
  const duplicated = useMemo(() => {
    const seen = new Map<string, string>();
    const pairs: string[] = [];
    (rows || []).forEach((row) => {
      const target = matches[row.clientName];
      if (!target || target === IGNORE) return;
      const previous = seen.get(target);
      if (previous) pairs.push(`«${previous}» y «${row.clientName}»`);
      else seen.set(target, row.clientName);
    });
    return pairs;
  }, [rows, matches]);

  /** Cambios contra el programa vigente, por celda. */
  const changes = useMemo(() => {
    const byCell = new Map<
      string,
      CommitmentChange & { kind: 'new' | 'updated' | 'deleted' }
    >();
    let unchanged = 0;
    for (const row of rows || []) {
      const target = matches[row.clientName];
      if (!target || target === IGNORE) continue;
      const receiverId = Number(target);
      row.values.forEach((value, week) => {
        const current = currentMatrix.get(receiverId)?.get(week) ?? 0;
        if (Math.abs(current - value) < 0.0001) {
          unchanged++;
          return;
        }
        const kind =
          value === 0 ? 'deleted' : current === 0 ? 'new' : 'updated';
        byCell.set(`${receiverId}-${week}`, {
          exporter_receiver_id: receiverId,
          week,
          quantity: value || null,
          kind,
        });
      });
    }
    const list = Array.from(byCell.values());
    return {
      list,
      created: list.filter((c) => c.kind === 'new').length,
      updated: list.filter((c) => c.kind === 'updated').length,
      deleted: list.filter((c) => c.kind === 'deleted').length,
      unchanged,
    };
  }, [rows, matches, currentMatrix]);

  const nameOf = (id: number) =>
    receivers.find((r) => r.id === id)?.name ?? String(id);

  const handleConfirm = async () => {
    if (changes.list.length === 0) return;
    setSaving(true);
    try {
      const result = await setCommitments(
        program.id,
        changes.list.map(({ exporter_receiver_id, week, quantity }) => ({
          exporter_receiver_id,
          week,
          quantity,
        }))
      );
      toast.success(
        t(
          'Programa actualizado: {{created}} nuevos, {{updated}} modificados, {{deleted}} borrados',
          {
            created: result.created,
            updated: result.updated,
            deleted: result.deleted,
          }
        )
      );
      await reloadProgramData();
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(t('No se pudo guardar: {{error}}', { error: message }));
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = (empty: boolean) =>
    exportProgramToExcel({
      program,
      receivers,
      weeks,
      matrix: currentMatrix,
      notes,
      season,
      speciesName: species?.name || '',
      t,
      emptyTemplate: empty,
    });

  const unitLabel = t(PROGRAM_UNIT_LABELS[program.unit]).toLowerCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader>{t('Importar programa desde Excel')}</ModalHeader>
        <ModalBody className='gap-4'>
          <div className='rounded-medium border border-default-200 p-4 text-sm text-default-500'>
            <p>
              {t(
                'Una fila por cliente y una columna por semana (S42, S43…), en {{unit}}.',
                { unit: unitLabel }
              )}{' '}
              {t(
                'Las celdas vacías no cambian nada; un 0 borra el compromiso.'
              )}
            </p>
            <div className='mt-2 flex flex-wrap gap-2'>
              <Button
                size='sm'
                variant='flat'
                onPress={() => downloadTemplate(false)}
                startContent={<Icon icon='tabler:download' width={16} />}
              >
                {t('Descargar con lo actual')}
              </Button>
              <Button
                size='sm'
                variant='light'
                onPress={() => downloadTemplate(true)}
              >
                {t('Plantilla vacía')}
              </Button>
            </div>
          </div>

          <input
            ref={fileRef}
            type='file'
            accept='.xlsx,.xls,.csv'
            className='hidden'
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
          <button
            type='button'
            onClick={() => fileRef.current?.click()}
            className='flex flex-col items-center gap-1 rounded-xl border-2 border-dashed border-default-300 p-6 text-sm text-default-500 hover:border-primary hover:text-primary'
          >
            <Icon icon='tabler:file-spreadsheet' width={28} />
            {fileName ? fileName : t('Elegir archivo Excel')}
          </button>

          {rows && (
            <>
              <div className='flex flex-wrap gap-2'>
                <StatusChip tone='success'>
                  {t('{{count}} nuevos', { count: changes.created })}
                </StatusChip>
                <StatusChip tone='warning'>
                  {t('{{count}} modificados', { count: changes.updated })}
                </StatusChip>
                <StatusChip tone='danger'>
                  {t('{{count}} borrados', { count: changes.deleted })}
                </StatusChip>
                <StatusChip>
                  {t('{{count}} sin cambios', { count: changes.unchanged })}
                </StatusChip>
              </div>

              {toReview.length > 0 && (
                <div className='flex flex-col gap-3 rounded-medium border border-default-200 p-4'>
                  {unmatched.length > 0 ? (
                    <Alert variant='warning' showIcon>
                      {t(
                        '{{count}} clientes del Excel no calzan con tus recibidores. Elige a cuál corresponde cada uno.',
                        { count: unmatched.length }
                      )}
                    </Alert>
                  ) : (
                    <Alert variant='default' showIcon>
                      {t(
                        'Calzamos {{count}} clientes por nombre parecido. Revisa que estén bien antes de aplicar.',
                        { count: similarCount }
                      )}
                    </Alert>
                  )}
                  {toReview.map((row) => (
                    <div
                      key={row.clientName}
                      className='grid grid-cols-1 items-center gap-2 sm:grid-cols-2'
                    >
                      <div className='flex min-w-0 items-center gap-2'>
                        <span
                          className='truncate text-sm font-medium'
                          title={row.clientName}
                        >
                          {row.clientName}
                        </span>
                        {matchKinds[row.clientName] === 'similar' ? (
                          <StatusChip tone='warning'>
                            {t('Parecido')}
                          </StatusChip>
                        ) : (
                          <StatusChip tone='danger'>
                            {t('Sin calzar')}
                          </StatusChip>
                        )}
                      </div>
                      <Select
                        size='sm'
                        label={t('Recibidor')}
                        placeholder={t('Elegir recibidor')}
                        selectedKeys={
                          matches[row.clientName]
                            ? [matches[row.clientName]]
                            : []
                        }
                        onSelectionChange={(keys) =>
                          setMatches((prev) => ({
                            ...prev,
                            [row.clientName]: String(Array.from(keys)[0] ?? ''),
                          }))
                        }
                      >
                        {[
                          <SelectItem key={IGNORE} value={IGNORE}>
                            {t('Ignorar esta fila')}
                          </SelectItem>,
                          ...receivers.map((r) => (
                            <SelectItem key={String(r.id)} value={String(r.id)}>
                              {r.name}
                            </SelectItem>
                          )),
                        ]}
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              {duplicated.length > 0 && (
                <Alert variant='danger' showIcon>
                  {t(
                    '{{pairs}} van al mismo recibidor. Elige otro o ignora una de las filas.',
                    { pairs: duplicated.join(', ') }
                  )}
                </Alert>
              )}

              {changes.list.length > 0 && (
                // Sin alto propio: el modal ya hace scroll (scrollBehavior='inside').
                <Table
                  aria-label={t('Cambios')}
                  removeWrapper
                  isCompact
                  isStriped
                >
                  <TableHeader>
                    <TableColumn>{t('Cliente')}</TableColumn>
                    <TableColumn align='center'>{t('Semana')}</TableColumn>
                    <TableColumn align='end'>{t('Antes')}</TableColumn>
                    <TableColumn align='end'>{t('Ahora')}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {changes.list.map((c) => {
                      const before =
                        currentMatrix
                          .get(c.exporter_receiver_id)
                          ?.get(c.week) ?? 0;
                      return (
                        <TableRow key={`${c.exporter_receiver_id}-${c.week}`}>
                          <TableCell>
                            {nameOf(c.exporter_receiver_id)}
                          </TableCell>
                          <TableCell className='text-center'>{`S${c.week}`}</TableCell>
                          <TableCell className='text-right text-default-400'>
                            {before
                              ? formatProgramQuantity(before, program.unit)
                              : '–'}
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium ${
                              c.kind === 'new'
                                ? 'text-success-600'
                                : c.kind === 'deleted'
                                  ? 'text-danger'
                                  : 'text-warning-600'
                            }`}
                          >
                            {c.quantity
                              ? formatProgramQuantity(c.quantity, program.unit)
                              : '–'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            {t('Cancelar')}
          </Button>
          <Button
            color='primary'
            onPress={handleConfirm}
            isLoading={saving}
            isDisabled={
              !rows ||
              changes.list.length === 0 ||
              unmatched.length > 0 ||
              duplicated.length > 0
            }
          >
            {t('Aplicar {{count}} cambios', { count: changes.list.length })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
